import { Middleware } from "@reduxjs/toolkit";
import {
  beginHistoryTransaction,
  cancelHistoryTransaction,
  commitHistory,
  commitHistoryTransaction,
  markCurrentDocumentSaved,
  markExternalDirty,
  rebaseCurrentDocument,
  rebaseHistory,
  resetHistory,
  restoreHistoryFrame,
  setSavedCheckpoint,
  setTransactionActive,
} from "./slices/HistorySlice";
import {
  cloneDocumentSnapshot,
  documentSnapshotsEqual,
  extractDocumentSnapshot,
  type DocumentSnapshot,
} from "./documentState";
import { getHistoryActionClassification } from "./undoableActions";
import {
  createHistoryFrame,
  type HistoryFrame,
  type HistoryRootState as EditorHistoryRootState,
} from "./historyState";

type HistoryRootState = EditorHistoryRootState & {
  history: {
    savedCheckpoint: DocumentSnapshot | null;
    transactionActive: boolean;
  };
};

const getDirtyAgainstCheckpoint = (
  current: DocumentSnapshot,
  checkpoint: DocumentSnapshot | null
) => checkpoint === null || !documentSnapshotsEqual(current, checkpoint);

export const createHistoryMiddleware = (): Middleware => {
  let transaction: { id: string; before: HistoryFrame } | undefined;

  return ({ getState, dispatch }) =>
    (next) =>
    (action) => {
      if (resetHistory.match(action)) {
        transaction = undefined;
        return next(action);
      }

      if (beginHistoryTransaction.match(action)) {
        if (transaction) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("Nested history transactions are not supported.");
          }
          return action;
        }

        transaction = {
          id: action.payload.id,
          before: createHistoryFrame(getState() as HistoryRootState),
        };
        const result = next(action);
        dispatch(setTransactionActive(true));
        return result;
      }

      if (commitHistoryTransaction.match(action)) {
        if (!transaction || transaction.id !== action.payload.id) return action;
        const activeTransaction = transaction;
        transaction = undefined;
        const result = next(action);
        const state = getState() as HistoryRootState;
        const after = createHistoryFrame(state);
        if (
          !documentSnapshotsEqual(
            activeTransaction.before.document,
            after.document
          )
        ) {
          dispatch(
            commitHistory({
              entry: { before: activeTransaction.before, after },
              documentDirty: getDirtyAgainstCheckpoint(
                after.document,
                state.history.savedCheckpoint
              ),
            })
          );
        }
        dispatch(setTransactionActive(false));
        return result;
      }

      if (cancelHistoryTransaction.match(action)) {
        if (!transaction || transaction.id !== action.payload.id) return action;
        const before = transaction.before;
        transaction = undefined;
        const result = next(action);
        dispatch(restoreHistoryFrame(before));
        dispatch(setTransactionActive(false));
        return result;
      }

      if (markCurrentDocumentSaved.match(action)) {
        const result = next(action);
        dispatch(
          setSavedCheckpoint(
            cloneDocumentSnapshot(
              extractDocumentSnapshot(getState() as HistoryRootState)
            )
          )
        );
        return result;
      }

      if (rebaseCurrentDocument.match(action)) {
        transaction = undefined;
        const result = next(action);
        dispatch(
          rebaseHistory(
            cloneDocumentSnapshot(
              extractDocumentSnapshot(getState() as HistoryRootState)
            )
          )
        );
        return result;
      }

      if (
        transaction &&
        (action.type === "history/undo" || action.type === "history/redo")
      ) {
        return action;
      }

      const classification = getHistoryActionClassification(action.type);
      if (classification === "external-dirty") {
        const result = next(action);
        dispatch(markExternalDirty());
        return result;
      }

      if (classification !== "command" || transaction) return next(action);

      const before = createHistoryFrame(getState() as HistoryRootState);
      const result = next(action);
      const state = getState() as HistoryRootState;
      const after = createHistoryFrame(state);

      if (!documentSnapshotsEqual(before.document, after.document)) {
        dispatch(
          commitHistory({
            entry: { before, after },
            documentDirty: getDirtyAgainstCheckpoint(
              after.document,
              state.history.savedCheckpoint
            ),
          })
        );
      }

      return result;
    };
};

export const historyMiddleware = createHistoryMiddleware();
